<script>
  import { onMount } from "svelte";
  import Icon from "@iconify/svelte";

  import dashboardIcon from "@iconify/icons-mdi/view-dashboard-outline";
  import keysIcon from "@iconify/icons-mdi/key-outline";
  import settingsIcon from "@iconify/icons-mdi/cog-outline";
  import runtimeIcon from "@iconify/icons-mdi/monitor-dashboard";

  import pauseIcon from "@iconify/icons-mdi/pause";
  import playIcon from "@iconify/icons-mdi/play";
  import refreshIcon from "@iconify/icons-mdi/refresh";
  import logoutIcon from "@iconify/icons-mdi/logout";
  import menuIcon from "@iconify/icons-mdi/menu";
  import closeIcon from "@iconify/icons-mdi/close";
  import { locales, t } from "./lib/i18n.js";
  import LanguageSwitcher from "./components/LanguageSwitcher.svelte";

  import KeyBrowser from "./KeyBrowser.svelte";
  import Settings from "./Settings.svelte";
  import Runtime from "./Runtime.svelte";
  import Login from "./Login.svelte";

  const DEV_PORTS = new Set(["5173", "4173"]);
  const isViteDev = location.hostname === "localhost" && DEV_PORTS.has(location.port);
  const API_BASE = isViteDev ? "http://localhost:8080" : "";

  const JSON_HEADERS = { Accept: "application/json" };
  const STORAGE_KEY = "stash_api_key";
  const LOCALE_STORAGE = "stash_locale";

  const AUTH_MODE_BOOT = (() => {
    if (typeof document === "undefined") return false;
    const s = document.querySelector('script[type="module"][data-authenticate]');
    const raw = (s?.dataset?.authenticate || "").toString().trim().toLowerCase();
    return raw === "true" || raw === "1" || raw === "yes" || raw === "on";
  })();

  let authMode = AUTH_MODE_BOOT;
  let screen = "keys";
  let locale = "en";

  let dashConnected = false;
  let dashPaused = false;
  let dashRefresh = null;

  let browseHosts = [];
  let selectedHostId = "";

  let menuOpen = false;

  let setup = null;
  let setupErr = "";
  let globalError = "";
  let globalErrorTimeout = null;

  let apiKey = "";
  let authed = !authMode;
  let booted = false;
  let pausedByAuth = false;
  let authErr = "";

  function readKey() {
    try {
      return (localStorage.getItem(STORAGE_KEY) || "").trim();
    } catch {
      return "";
    }
  }

  function readLocale() {
    try {
      const value = (localStorage.getItem(LOCALE_STORAGE) || "en").toLowerCase();
      return locales.includes(value) ? value : "en";
    } catch {
      return "en";
    }
  }

  function setKey(next) {
    const v = (next || "").trim();
    try {
      if (!v) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, v);
      window.dispatchEvent(new Event("stash-auth-changed"));
    } catch {}
  }

  function syncAuthFromStorage() {
    apiKey = readKey();
    authed = !authMode || apiKey !== "";

    if (!authed) {
      pausedByAuth = true;
      dashPaused = true;
      dashConnected = false;
      return;
    }

    if (pausedByAuth) {
      dashPaused = false;
      pausedByAuth = false;
    }
  }

  function authHeaders() {
    return apiKey ? { Authorization: `Bearer ${apiKey}` } : {};
  }

  function onUnauthorized(msg = t(locale, "auth_required")) {
    authErr = msg;
    authMode = true;
    authed = false;
    pausedByAuth = true;
    dashPaused = true;
    dashConnected = false;
    setup = null;
    setupErr = "";
    screen = "keys";
    setKey("");
    apiKey = "";
  }

  function handleLocaleChange(code) {
    locale = locales.includes(code) ? code : "en";
    try {
      localStorage.setItem(LOCALE_STORAGE, locale);
    } catch {}
  }

  function go(to) {
    screen = to;
    menuOpen = false;
  }

  function doRefresh() {
    if (typeof dashRefresh === "function") dashRefresh();
  }

  function togglePause() {
    dashPaused = !dashPaused;
  }

  async function prefetchSetup() {
    setupErr = "";
    globalError = "";
    try {
      const res = await fetch(`${API_BASE}/setup`, {
        method: "GET",
        cache: "no-store",
        headers: { ...JSON_HEADERS, ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized(apiKey ? t(locale, "login_invalid_key") : t(locale, "auth_required"));
        return;
      }

      if (!res.ok) {
        globalError = t(locale, "setup_failed", { status: res.status });
        return;
      }

      setup = await res.json();
      if (authMode) authed = true;

      const hostsRes = await fetch(`${API_BASE}/browse/hosts`, {
        cache: "no-store",
        headers: { ...JSON_HEADERS, ...authHeaders() }
      });
      if (hostsRes.ok) {
        const data = await hostsRes.json();
        browseHosts = data.hosts || [];
        if (browseHosts.length > 0 && !selectedHostId) {
          selectedHostId = browseHosts[0].id;
        }
      }
    } catch (e) {
      setup = null;
      setupErr = e?.message || String(e);
    }
  }

  function setGlobalError(msg) {
    if (globalErrorTimeout) clearTimeout(globalErrorTimeout);
    globalError = msg || "";
    if (globalError) {
      globalErrorTimeout = setTimeout(() => {
        globalError = "";
        globalErrorTimeout = null;
      }, 20000);
    }
  }

  function onHostChange(id) {
    selectedHostId = id;
  }

  onMount(() => {
    locale = readLocale();
    syncAuthFromStorage();
    booted = true;

    const onStorage = (e) => {
      if (e?.key === STORAGE_KEY) syncAuthFromStorage();
      if (e?.key === LOCALE_STORAGE) locale = readLocale();
    };
    const onCustom = () => syncAuthFromStorage();

    window.addEventListener("storage", onStorage);
    window.addEventListener("stash-auth-changed", onCustom);

    prefetchSetup();

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("stash-auth-changed", onCustom);
    };
  });
  
  $: if (authMode && !authed) {
    screen = "keys";
    setup = null;
    setupErr = "";
  }

  let didPrefetchAfterLogin = false;
  $: if (authed && !didPrefetchAfterLogin) {
    didPrefetchAfterLogin = true;
    prefetchSetup();
  }
  $: if (!authed) {
    didPrefetchAfterLogin = false;
  }

  const iconBtnBase =
    "inline-flex items-center justify-center w-10 h-10 rounded-lg border " +
    "transition-all select-none " +
    "border-white/10 bg-white/[0.035] hover:bg-white/[0.06] " +
    "text-zinc-100/90 hover:text-white";

  const dividerCls = "w-px h-6 bg-white/10 mx-2";
</script>

{#if authMode && !authed}
  <!-- Unauthenticated: plain background only, no dashboard or gradient -->
  <div class="min-h-screen w-full bg-black text-zinc-100">
    {#if !booted}
      <div class="min-h-screen grid place-items-center text-zinc-400 text-sm">{t(locale, "loading")}</div>
    {:else}
    <Login
      apiBase={API_BASE}
      jsonHeaders={JSON_HEADERS}
      storageKey={STORAGE_KEY}
      error={authErr}
      {locale}
      storeEngine={setup?.engine || setup?.store_engine || ""}
      onLocaleChange={handleLocaleChange}
      onSave={(k) => {
        authErr = "";
          setKey(k);
        }}
      />
    {/if}
  </div>
{:else}
  <div class="min-h-screen w-full bg-black text-zinc-100 flex flex-col">
    <div class="pointer-events-none fixed inset-0 -z-10">
      <div class="absolute inset-0 bg-[radial-gradient(900px_520px_at_85%_10%,rgba(255,255,255,0.08),transparent_62%)]"></div>
      <div class="absolute inset-0 bg-gradient-to-b from-[#07070a] via-[#050508] to-black"></div>
    </div>

    {#if !booted}
      <div class="min-h-screen grid place-items-center text-zinc-400 text-sm">
        {t(locale, "loading")}
      </div>
    {:else}
    <div class="w-full pt-4 px-4 sm:px-6">
      <header class="sticky top-3 z-50 ui-topbar w-full">
        <div class="w-full px-3 py-2 sm:px-4 sm:py-3">
          <div class="flex items-center justify-between gap-2">
            <!-- Logo + host (always visible) -->
            <div class="flex items-center gap-2 min-w-0 flex-1">
              <button type="button" class="flex items-center gap-2 min-w-0 cursor-pointer text-left" onclick={() => go("keys")}>
                <div class="ui-logo text-semibold flex-shrink-0" aria-hidden="true">
                  <Icon icon="streamline-ultimate:warehouse-cart-packages-2-bold" width="18" height="18" />
                </div>
                <div class="flex flex-col justify-center leading-tight min-w-0 hidden sm:flex">
                  <div class="text-base sm:text-lg font-semibold tracking-wide text-white truncate">{t(locale, "app_name")}</div>
                  <div class="text-xs text-zinc-400 truncate">{setup?.store_engine || t(locale, "app_subtitle")}</div>
                </div>
              </button>
              <div class="flex items-center gap-2 ml-1 sm:ml-2 min-w-0 ms-8">
                <label for="host-select" class="sr-only">{t(locale, "store_host")}</label>
                <select
                  id="host-select"
                  class="h-10 rounded-lg border border-white/10 bg-white/[0.06] px-2 sm:px-3 text-xs sm:text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-red-400/30 min-w-0 max-w-[120px] sm:max-w-none sm:min-w-[140px]"
                  bind:value={selectedHostId}
                  onchange={() => onHostChange(selectedHostId)}
                >
                  {#each browseHosts as h}
                    <option value={h.id}>{h.label}</option>
                  {/each}
                </select>
              </div>
            </div>

            <!-- Desktop: nav + controls -->
            <div class="hidden lg:flex flex-nowrap items-center justify-end gap-3">
              <div class="flex items-center gap-2 h-10">
                <div class="inline-flex h-10 items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3">
                  <span class={`h-2.5 w-2.5 rounded-full ${dashConnected ? "bg-emerald-400" : "bg-rose-400"}`}></span>
                  <span class="text-sm text-zinc-200/90">{dashConnected ? t(locale, "connected") : t(locale, "disconnected")}</span>
                </div>
                <button type="button" class={`${iconBtnBase} ${dashPaused ? "opacity-80" : ""}`} title={dashPaused ? t(locale, "resume") : t(locale, "pause")} onclick={togglePause}>
                  <Icon icon={dashPaused ? playIcon : pauseIcon} width="18" height="18" />
                </button>
                <button type="button" class={iconBtnBase} title={t(locale, "refresh")} onclick={doRefresh}>
                  <Icon icon={refreshIcon} width="18" height="18" />
                </button>
                <div class={dividerCls}></div>
              </div>
              <nav class="topnav topnav--nowrap h-10">
                
                <button class="topnav__tab" class:is-active={screen === "settings"} onclick={() => go("settings")}><Icon icon={settingsIcon} width="16" height="16" /><span>{t(locale, "nav_settings")}</span></button>
                <button class="topnav__tab" class:is-active={screen === "runtime"} onclick={() => go("runtime")}><Icon icon={runtimeIcon} width="16" height="16" /><span>{t(locale, "nav_runtime")}</span></button>
              </nav>
              <LanguageSwitcher bind:locale onchange={handleLocaleChange} />
              {#if authMode}
                <div class="flex h-10 items-center gap-2">
                  <div class={dividerCls}></div>
                  <button type="button" class="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] transition-all" title={t(locale, "logout")} aria-label={t(locale, "logout")} onclick={() => { authErr = ""; setKey(""); }}>
                    <Icon icon={logoutIcon} width="18" height="18" />
                  </button>
                </div>
              {/if}
            </div>

            <!-- Mobile: hamburger -->
            <button
              type="button"
              class="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] text-zinc-200"
              aria-label={menuOpen ? t(locale, "close_menu") : t(locale, "open_menu")}
              onclick={() => (menuOpen = !menuOpen)}
            >
              <Icon icon={menuOpen ? closeIcon : menuIcon} width="24" height="24" />
            </button>
          </div>

          <!-- Mobile: no inline menu here; full overlay is below -->
        </div>
      </header>
    </div>

    <!-- Mobile: full overlay menu (not collapsible header) -->
    {#if menuOpen}
      <div
        class="lg:hidden fixed inset-0 z-[100] flex"
        role="dialog"
        aria-label={t(locale, "menu")}
      >
        <button
          type="button"
          class="absolute inset-0 bg-black/70 backdrop-blur-sm"
          aria-label={t(locale, "close_menu")}
          onclick={() => (menuOpen = false)}
        ></button>
        <div class="relative w-full max-w-sm ml-auto bg-zinc-900 border-l border-white/10 shadow-xl flex flex-col p-4">
          <div class="flex items-center justify-between mb-4">
            <span class="text-lg font-semibold text-white">{t(locale, "menu")}</span>
            <button type="button" class="p-2 text-zinc-400 hover:text-white" onclick={() => (menuOpen = false)} aria-label={t(locale, "close_menu")}>
              <Icon icon={closeIcon} width="24" height="24" />
            </button>
          </div>
          <nav class="flex flex-col gap-1">
            <button class="topnav__tab w-full justify-center" class:is-active={screen === "keys"} onclick={() => go("keys")}><Icon icon={keysIcon} width="16" height="16" /><span>{t(locale, "nav_browse")}</span></button>
            <button class="topnav__tab w-full justify-center" class:is-active={screen === "settings"} onclick={() => go("settings")}><Icon icon={settingsIcon} width="16" height="16" /><span>{t(locale, "nav_settings")}</span></button>
            <button class="topnav__tab w-full justify-center" class:is-active={screen === "runtime"} onclick={() => go("runtime")}><Icon icon={runtimeIcon} width="16" height="16" /><span>{t(locale, "nav_runtime")}</span></button>
          </nav>
          <div class="mt-3">
            <LanguageSwitcher bind:locale onchange={handleLocaleChange} />
          </div>
          {#if authMode}
            <button type="button" class="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] py-2.5 text-sm text-zinc-200" onclick={() => { authErr = ""; setKey(""); menuOpen = false; }}>
              <Icon icon={logoutIcon} width="18" height="18" /> {t(locale, "logout")}
            </button>
          {/if}
        </div>
      </div>
    {/if}

    {#if globalError}
      {@const isKeyNotFound = globalError.includes('Key not found') || globalError.includes('may have been removed')}
      <div class="w-full px-4 sm:px-6 py-2">
        <div class="rounded-lg text-sm flex items-center justify-between gap-2 px-4 py-2 {isKeyNotFound ? 'bg-fuchsia-500/15 border-fuchsia-400/30 text-fuchsia-200 border' : 'bg-rose-500/15 border border-rose-400/30 text-rose-200'}" role="alert">
          <span>{globalError}</span>
          <button type="button" class="shrink-0 p-1 rounded {isKeyNotFound ? 'hover:bg-fuchsia-400/20' : 'hover:bg-rose-400/20'}" onclick={() => setGlobalError('')} aria-label={t(locale, "dismiss")}>×</button>
        </div>
      </div>
    {/if}

    {#if screen === "keys"}
      <div class="flex-1 min-h-0 flex flex-col w-full px-4 sm:px-6">
      <KeyBrowser
        apiBase={API_BASE}
        selectedHostId={selectedHostId}
        {apiKey}
        {locale}
        {authMode}
        bind:connected={dashConnected}
        bind:paused={dashPaused}
        setRefresh={(fn) => (dashRefresh = fn)}
        onApiError={setGlobalError}
        {onUnauthorized}
      />
      </div>
    {:else if screen === "settings"}
      <div class="w-full px-4 sm:px-6">
        <Settings apiBase={API_BASE} {apiKey} {setup} setupErr={setupErr} browseHosts={browseHosts} {locale} onApiError={setGlobalError} />
      </div>
    {:else if screen === "runtime"}
      <div class="w-full px-4 sm:px-6">
        <Runtime apiBase={API_BASE} {apiKey} {locale} />
      </div>
    {/if}
    {/if}
  </div>
{/if}
